import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

// ── Document types ────────────────────────────────────────────────────────────
const DOCS = [
  { id: 'terms',    label: 'Terms of Service',   icon: '📜', description: 'Rules users agree to when using Kudos Live Market' },
  { id: 'privacy',  label: 'Privacy Policy',     icon: '🔒', description: 'How we collect, use and protect user data' },
  { id: 'seller',   label: 'Seller Agreement',   icon: '🏪', description: 'Terms and conditions for shop owners' },
  { id: 'driver',   label: 'Driver Agreement',   icon: '🚚', description: 'Terms and conditions for delivery drivers' },
  { id: 'refund',   label: 'Refund Policy',      icon: '↩',  description: 'How refunds and returns are handled' },
  { id: 'cookies',  label: 'Cookie Policy',      icon: '🍪', description: 'Our use of cookies and tracking technologies' },
]

// ── Simple toolbar button ─────────────────────────────────────────────────────
function ToolBtn({ label, onClick, active, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        padding: '5px 10px', borderRadius: 5,
        border: active ? '1px solid #111317' : '1px solid transparent',
        background: active ? '#111317' : 'transparent',
        color: active ? '#fff' : '#374151',
        fontSize: 12, fontWeight: 700, cursor: 'pointer',
        fontFamily: "'Plus Jakarta Sans',sans-serif",
        lineHeight: 1,
      }}>
      {label}
    </button>
  )
}

// ── Version History Modal ─────────────────────────────────────────────────────
function HistoryModal({ docType, onClose, onRestore }) {
  const [versions, setVersions] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [preview,  setPreview]  = useState(null)

  useEffect(() => {
    api.get(`/terms/admin/versions/${docType}`)
      .then(res => setVersions(res.data.versions || []))
      .catch(() => setVersions([
        { version_id: 3, version: '1.2', published: true,  published_at: new Date(Date.now()-86400000*2).toISOString(), updated_by: 'Admin', word_count: 1240 },
        { version_id: 2, version: '1.1', published: false, published_at: new Date(Date.now()-86400000*30).toISOString(), updated_by: 'Admin', word_count: 1100 },
        { version_id: 1, version: '1.0', published: false, published_at: new Date(Date.now()-86400000*90).toISOString(), updated_by: 'Admin', word_count: 980  },
      ]))
      .finally(() => setLoading(false))
  }, [docType])

  const doc = DOCS.find(d => d.id === docType)

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.22)', zIndex:300 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:580, maxHeight:'80vh', display:'flex', flexDirection:'column', background:'#fff', borderRadius:14, zIndex:301, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', animation:'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800 }}>Version History</div>
            <div style={{ fontSize:12, color:'#9a9a94', marginTop:2 }}>{doc?.icon} {doc?.label}</div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94' }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:'32px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>Loading…</div>
          ) : (
            versions.map((v, i) => (
              <div key={v.version_id} style={{ padding:'14px 22px', borderBottom:i<versions.length-1?'1px solid #f3f3f0':'none', display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:13, fontWeight:800 }}>v{v.version}</span>
                    {v.published && (
                      <span style={{ fontSize:9, fontWeight:700, background:'#f0fdf4', color:'#15803d', padding:'2px 7px', borderRadius:3, fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Published</span>
                    )}
                  </div>
                  <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginTop:3 }}>
                    {fmtTime(v.published_at)} · by {v.updated_by} · {v.word_count} words
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setPreview(v)}
                    style={{ padding:'5px 12px', borderRadius:6, border:'1px solid #e9e9e4', background:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Preview</button>
                  {!v.published && (
                    <button onClick={() => { onRestore(v); onClose() }}
                      style={{ padding:'5px 12px', borderRadius:6, border:'none', background:'#f0fdf4', color:'#15803d', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Restore</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Version preview overlay */}
      {preview && (
        <>
          <div onClick={() => setPreview(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:400 }} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:640, maxHeight:'80vh', background:'#fff', borderRadius:14, zIndex:401, boxShadow:'0 20px 60px rgba(0,0,0,0.2)', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, fontWeight:800 }}>Preview — v{preview.version}</span>
              <div onClick={() => setPreview(null)} style={{ cursor:'pointer', fontSize:18, color:'#9a9a94' }}>×</div>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:20 }}>
              <div style={{ fontSize:13, color:'#374151', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{preview.content || 'No preview available for this version.'}</div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ── Document Editor ───────────────────────────────────────────────────────────
function DocEditor({ docType, onBack }) {
  const [content,     setContent]     = useState('')
  const [version,     setVersion]     = useState('1.0')
  const [effectiveDate, setEffDate]   = useState(new Date().toISOString().slice(0,10))
  const [loading,     setLoading]     = useState(true)
  const [saving,      setSaving]      = useState(false)
  const [publishing,  setPublishing]  = useState(false)
  const [saved,       setSaved]       = useState(false)
  const [published,   setPublished]   = useState(false)
  const [lastSaved,   setLastSaved]   = useState(null)
  const [isPublished, setIsPublished] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [wordCount,   setWordCount]   = useState(0)
  const textareaRef = useRef(null)
  const saveTimerRef = useRef(null)

  const doc = DOCS.find(d => d.id === docType)

  useEffect(() => {
    setLoading(true)
    api.get(`/terms/admin/${docType}`)
      .then(res => {
        const d = res.data.document
        setContent(d?.content || getDefaultContent(docType))
        setVersion(d?.version || '1.0')
        setEffDate(d?.effective_date?.slice(0,10) || new Date().toISOString().slice(0,10))
        setIsPublished(!!d?.published)
        setLastSaved(d?.updated_at || null)
      })
      .catch(() => {
        setContent(getDefaultContent(docType))
      })
      .finally(() => setLoading(false))
  }, [docType])

  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(Boolean).length
    setWordCount(words)
  }, [content])

  // Auto-save draft every 30s
  useEffect(() => {
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      if (content && !loading) handleSave(true)
    }, 30000)
    return () => clearTimeout(saveTimerRef.current)
  }, [content])

  const handleSave = async (auto = false) => {
    setSaving(true); setSaved(false)
    try {
      await api.put(`/terms/admin/${docType}`, { content, version, effective_date: effectiveDate })
      setSaved(true)
      setLastSaved(new Date().toISOString())
      if (!auto) setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      if (!auto) alert(err.response?.data?.error || 'Failed to save')
    } finally { setSaving(false) }
  }

  const handlePublish = async () => {
    if (!content.trim()) return alert('Cannot publish an empty document.')
    if (!window.confirm(`Publish this version of ${doc?.label}? It will be visible to all users immediately.`)) return
    setPublishing(true)
    try {
      // Ensure draft is saved before publishing
      await api.put(`/terms/admin/${docType}`, { content, version, effective_date: effectiveDate })
      setLastSaved(new Date().toISOString())
      await api.post(`/terms/admin/${docType}/publish`)
      setIsPublished(true)
      setPublished(true)
      setTimeout(() => setPublished(false), 3000)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to publish')
    } finally { setPublishing(false) }
  }

  const handleRestore = async (v) => {
    if (v.content) setContent(v.content)
    setVersion(v.version + '-restored')
  }

  // Toolbar formatting helpers
  const insertAround = (before, after = before) => {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart
    const end   = el.selectionEnd
    const sel   = content.slice(start, end)
    const newContent = content.slice(0, start) + before + sel + after + content.slice(end)
    setContent(newContent)
    setTimeout(() => {
      el.selectionStart = start + before.length
      el.selectionEnd   = end + before.length
      el.focus()
    }, 0)
  }

  const insertAtLineStart = (prefix) => {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart
    const lineStart = content.lastIndexOf('\n', start - 1) + 1
    setContent(content.slice(0, lineStart) + prefix + content.slice(lineStart))
    setTimeout(() => el.focus(), 0)
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 140px)' }}>
      {/* Editor header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14, flexWrap:'wrap' }}>
        {/* Back */}
        <button onClick={onBack} style={{ padding:'6px 12px', borderRadius:7, border:'1px solid #e9e9e4', background:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', gap:6 }}>
          ← Back
        </button>

        <div style={{ fontSize:20 }}>{doc?.icon}</div>
        <div>
          <div style={{ fontSize:15, fontWeight:800 }}>{doc?.label}</div>
          <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
            {wordCount.toLocaleString()} words
            {lastSaved && ` · Last saved: ${fmtTime(lastSaved)}`}
          </div>
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
          {/* Version + effective date */}
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>v</span>
            <input value={version} onChange={e => setVersion(e.target.value)}
              style={{ width:60, height:34, borderRadius:7, border:'1px solid #e9e9e4', padding:'0 8px', fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", outline:'none', textAlign:'center' }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:11, color:'#9a9a94' }}>Effective</span>
            <input type="date" value={effectiveDate} onChange={e => setEffDate(e.target.value)}
              style={{ height:34, borderRadius:7, border:'1px solid #e9e9e4', padding:'0 8px', fontSize:12, fontFamily:"'JetBrains Mono',monospace", outline:'none' }} />
          </div>

          {/* History */}
          <button onClick={() => setShowHistory(true)}
            style={{ height:34, padding:'0 14px', borderRadius:7, border:'1px solid #e9e9e4', background:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            🕐 History
          </button>

          {/* Save */}
          <button onClick={() => handleSave(false)} disabled={saving}
            style={{ height:34, padding:'0 16px', borderRadius:7, border:'1px solid #e9e9e4', background:saved?'#f0fdf4':saving?'#f9f9f7':'#fff', color:saved?'#15803d':'#111317', fontSize:12, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Draft'}
          </button>

          {/* Publish */}
          <button onClick={handlePublish} disabled={publishing || isPublished}
            style={{ height:34, padding:'0 18px', borderRadius:7, border:'none', background:published?'#15803d':isPublished?'#f0fdf4':publishing?'#f0c4b4':'#e8440a', color:isPublished&&!published?'#15803d':'#fff', fontSize:12, fontWeight:700, cursor:publishing||isPublished?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:isPublished?'none':'0 2px 8px rgba(232,68,10,0.2)' }}>
            {published ? '✓ Published!' : isPublished ? '✓ Live' : publishing ? 'Publishing…' : '⬆ Publish'}
          </button>
        </div>
      </div>

      {/* Published status strip */}
      {isPublished && (
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'8px 16px', marginBottom:10, fontSize:12, color:'#15803d', display:'flex', alignItems:'center', gap:8 }}>
          <span>✓</span>
          <span><b>This version is currently live</b> and visible to all users in the app.</span>
          <span onClick={() => { setIsPublished(false); setVersion(v => v + '.1') }} style={{ marginLeft:'auto', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Create New Draft →</span>
        </div>
      )}

      {/* Formatting toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:4, padding:'8px 12px', background:'#fff', border:'1px solid #e9e9e4', borderRadius:'8px 8px 0 0', borderBottom:'none', flexWrap:'wrap' }}>
        <ToolBtn label="H1" onClick={() => insertAtLineStart('# ')}        title="Heading 1" />
        <ToolBtn label="H2" onClick={() => insertAtLineStart('## ')}       title="Heading 2" />
        <ToolBtn label="H3" onClick={() => insertAtLineStart('### ')}      title="Heading 3" />
        <div style={{ width:1, height:18, background:'#e9e9e4', margin:'0 4px' }} />
        <ToolBtn label="B"  onClick={() => insertAround('**')}             title="Bold"  />
        <ToolBtn label="I"  onClick={() => insertAround('_')}              title="Italic" />
        <div style={{ width:1, height:18, background:'#e9e9e4', margin:'0 4px' }} />
        <ToolBtn label="• List"  onClick={() => insertAtLineStart('- ')}   title="Bullet list" />
        <ToolBtn label="1. List" onClick={() => insertAtLineStart('1. ')}  title="Numbered list" />
        <ToolBtn label="---"     onClick={() => setContent(c => c + '\n---\n')} title="Divider" />
        <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
          <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", padding:'5px 0' }}>Markdown supported</span>
        </div>
      </div>

      {/* Editor + preview pane */}
      <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', borderRadius:'0 0 10px 10px', border:'1px solid #e9e9e4', overflow:'hidden', minHeight:0 }}>
        {/* Raw editor */}
        <div style={{ display:'flex', flexDirection:'column', borderRight:'1px solid #e9e9e4' }}>
          <div style={{ padding:'8px 14px', background:'#f9f9f7', borderBottom:'1px solid #e9e9e4', fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em' }}>
            Editor (Markdown)
          </div>
          {loading ? (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#9a9a94', fontSize:13 }}>Loading…</div>
          ) : (
            <textarea
              ref={textareaRef}
              value={content}
              onChange={e => setContent(e.target.value)}
              style={{ flex:1, border:'none', outline:'none', padding:'16px 18px', fontSize:13, fontFamily:"'JetBrains Mono',monospace", lineHeight:1.7, resize:'none', color:'#111317', background:'#fff' }}
            />
          )}
        </div>

        {/* Live preview */}
        <div style={{ display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ padding:'8px 14px', background:'#f9f9f7', borderBottom:'1px solid #e9e9e4', fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em' }}>
            Preview
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
            <MarkdownPreview content={content} />
          </div>
        </div>
      </div>

      {showHistory && <HistoryModal docType={docType} onClose={() => setShowHistory(false)} onRestore={handleRestore} />}
    </div>
  )
}

// ── Markdown Preview ──────────────────────────────────────────────────────────
function MarkdownPreview({ content }) {
  const html = content
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3 style="font-size:14px;font-weight:800;margin:20px 0 8px;color:#111317">$1</h3>')
    .replace(/^## (.+)$/gm,  '<h2 style="font-size:16px;font-weight:800;margin:24px 0 10px;color:#111317">$1</h2>')
    .replace(/^# (.+)$/gm,   '<h1 style="font-size:20px;font-weight:800;margin:0 0 16px;color:#111317">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e9e9e4;margin:20px 0">')
    .replace(/^- (.+)$/gm, '<li style="margin:4px 0;padding-left:4px">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li style="margin:4px 0;list-style-type:decimal;padding-left:4px">$2</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (m) => `<ul style="padding-left:20px;margin:8px 0">${m}</ul>`)
    .replace(/^(?!<[h|l|u|h|d|p]).+$/gm, (line) => line.trim() ? `<p style="margin:8px 0;line-height:1.8;color:#374151">${line}</p>` : '')

  return <div style={{ fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif" }} dangerouslySetInnerHTML={{ __html: html }} />
}

// ── Default content templates ─────────────────────────────────────────────────
function getDefaultContent(type) {
  const templates = {
    terms: `# Terms of Service

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. Acceptance of Terms

By accessing or using Kudos Live Market, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.

## 2. Description of Service

Kudos Live Market is a shoppable live streaming and reels platform that connects buyers with sellers and delivery drivers in Rwanda.

## 3. User Accounts

- You must be at least 18 years old to create an account.
- You are responsible for maintaining the confidentiality of your account credentials.
- You agree to provide accurate and complete information when creating your account.

## 4. Seller Responsibilities

Sellers are responsible for:
- Accuracy of product listings and prices
- Timely order fulfillment
- Compliance with Rwandan business regulations

## 5. Payments and Fees

All transactions are processed through approved payment providers including MTN Mobile Money, Airtel Money, and iPay. Platform fees of 1% apply to all completed transactions.

## 6. Prohibited Activities

Users may not:
- Post false or misleading product information
- Engage in fraudulent transactions
- Harass other users or platform staff

## 7. Changes to Terms

We reserve the right to update these terms at any time. Continued use of the platform constitutes acceptance of the updated terms.

## 8. Contact

For questions about these terms, contact us at legal@kudos.rw`,

    privacy: `# Privacy Policy

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. Introduction

Kudos Live Market ("we", "our", or "us") is committed to protecting your personal information. This policy explains how we collect, use, and safeguard your data.

## 2. Information We Collect

- **Account information:** Name, phone number, email address
- **Transaction data:** Order history, payment methods, delivery addresses
- **Usage data:** App interactions, viewed reels, followed shops
- **Device information:** Device type, operating system, push notification tokens

## 3. How We Use Your Information

We use your information to:
- Process orders and payments
- Send delivery and order status updates
- Personalize your feed and recommendations
- Send important service notifications

## 4. Data Sharing

We share your data with:
- **Sellers:** Order details necessary for fulfillment
- **Drivers:** Delivery address and contact information
- **Payment providers:** Transaction data for MTN MoMo, Airtel, and iPay

We do not sell your personal data to third parties.

## 5. Data Security

We implement industry-standard security measures including encryption at rest and in transit.

## 6. Your Rights

You have the right to:
- Access your personal data
- Correct inaccurate information
- Request deletion of your account

## 7. Contact

For privacy inquiries, contact privacy@kudos.rw`,

    seller: `# Seller Agreement

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. Seller Eligibility

To become a seller on Kudos Live Market, you must:
- Have a valid Rwandan business registration or be an individual trader
- Provide accurate shop and product information
- Accept our platform commission of 1% per transaction

## 2. Product Listings

All products must comply with Rwandan law and our content guidelines.

## 3. Order Fulfillment

Sellers must fulfill orders within the agreed timeframe and maintain adequate stock levels.

## 4. Payments

Seller payouts are processed monthly after deducting platform fees and iPay processing fees.`,

    driver: `# Driver Agreement

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. Driver Requirements

To work as a delivery driver on Kudos Live Market, you must:
- Hold a valid Rwandan driver's license
- Own or have legal access to a delivery vehicle
- Maintain active vehicle insurance

## 2. Delivery Standards

Drivers must complete deliveries in a timely, professional manner and maintain their rating above 3.5 stars.

## 3. Earnings

Delivery fees are set by zone and are paid per completed delivery.`,

    refund: `# Refund Policy

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. Eligibility for Refunds

Refunds are available for:
- Items not received within the stated delivery window
- Items significantly different from the product description
- Damaged items upon delivery

## 2. Refund Process

To request a refund, contact support within 48 hours of the expected delivery date.

## 3. Processing Time

Approved refunds are processed within 3–7 business days to your original payment method.`,

    cookies: `# Cookie Policy

**Effective Date:** ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}

## 1. What Are Cookies

Cookies are small text files stored on your device that help us improve your experience.

## 2. How We Use Cookies

- **Essential cookies:** Required for the app to function
- **Analytics cookies:** Help us understand how users interact with the platform
- **Preference cookies:** Remember your settings and preferences

## 3. Managing Cookies

You can control cookie settings through your device settings.`,
  }
  return templates[type] || `# ${DOCS.find(d=>d.id===type)?.label}\n\nStart writing your document here…`
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Terms() {
  const [activeDoc, setActiveDoc] = useState(null)
  const [docStatus, setDocStatus] = useState({})

  // Fetch published status for all docs
  useEffect(() => {
    Promise.all(
      DOCS.map(d =>
        api.get(`/terms/admin/${d.id}`)
          .then(res => ({ id: d.id, ...res.data.document }))
          .catch(() => ({ id: d.id }))
      )
    ).then(results => {
      const status = {}
      results.forEach(r => { status[r.id] = r })
      setDocStatus(status)
    })
  }, [])

  if (activeDoc) {
    return (
      <div style={{ padding:'28px 32px 60px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
        <DocEditor docType={activeDoc} onBack={() => setActiveDoc(null)} />
      </div>
    )
  }

  return (
    <div style={{ padding:'28px 32px 60px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>Terms & Legal Documents</div>
        <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>Edit and publish all legal documents shown in the app. Changes go live immediately upon publishing.</div>
      </div>

      {/* Document cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
        {DOCS.map(doc => {
          const status  = docStatus[doc.id]
          const hasDoc  = !!status?.content
          const isLive  = !!status?.published
          const version = status?.version

          return (
            <div key={doc.id}
              onClick={() => setActiveDoc(doc.id)}
              style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, padding:22, cursor:'pointer', transition:'all 0.15s', position:'relative' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform='translateY(-2px)' }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none' }}>

              {/* Live indicator */}
              {isLive && (
                <div style={{ position:'absolute', top:16, right:16 }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#f0fdf4', color:'#15803d', fontSize:9, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", padding:'3px 8px', borderRadius:4, textTransform:'uppercase' }}>
                    <span style={{ width:5, height:5, borderRadius:'50%', background:'#16a34a' }} />Live
                  </span>
                </div>
              )}
              {!isLive && hasDoc && (
                <div style={{ position:'absolute', top:16, right:16 }}>
                  <span style={{ fontSize:9, fontWeight:700, background:'#fffbeb', color:'#b45309', padding:'3px 8px', borderRadius:4, fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Draft</span>
                </div>
              )}

              <div style={{ fontSize:28, marginBottom:12 }}>{doc.icon}</div>
              <div style={{ fontSize:15, fontWeight:800, marginBottom:5 }}>{doc.label}</div>
              <div style={{ fontSize:12, color:'#9a9a94', lineHeight:1.5, marginBottom:14 }}>{doc.description}</div>

              {/* Meta */}
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                {version && (
                  <span style={{ fontSize:11, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace" }}>v{version}</span>
                )}
                {status?.effective_date && (
                  <span style={{ fontSize:11, color:'#6b7280' }}>Effective {fmt(status.effective_date)}</span>
                )}
                {status?.word_count && (
                  <span style={{ fontSize:11, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace" }}>{Number(status.word_count).toLocaleString()} words</span>
                )}
              </div>

              <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#e8440a' }}>
                  {hasDoc ? 'Edit Document →' : 'Create Document →'}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}